import type { FormContext } from "@custom-types/form-types";
import { paths } from "@utils/navigation";
import { NOMINATION_DOCUMENTS_FIELD } from "../utils";
import type { VesselNominationFormData } from "./schema/vesselNominationFormSchema";

export type VesselNominationOutletContext = FormContext<VesselNominationFormData>;

const { vesselNomination } = paths;

export const steps = [
  {
    label: "vessel-nomination.add.steps.pre-arrival-questionnaire",
    buttonLabel: "vessel-nomination.add.steps.paq",
    path: vesselNomination.preArrivalQuestionnaire,
  },
  {
    label: "vessel-nomination.add.steps.documents",
    path: vesselNomination.documents,
  },
];

export const FORM_FIELDS = {
  VESSEL_ID: "vesselId",
  ETA: "eta",
  RTA: "rta",
  CALL_SIGN: "callSign",
  PREVIOUS_VESSEL_NAME: "previousVesselName",
  LOA: "loa",
  BALLAST: "ballast",
  DRAUGHT_SUMMER: "draughtSummer",
  DRAUGHT_TROPICAL: "draughtTropical",
  ARRIVAL_FWD: "arrivalFwd",
  ARRIVAL_MID: "arrivalMid",
  ARRIVAL_AFT: "arrivalAft",
  MAX_TRIM: "maxTrim",
  DEPARTURE_FWD: "departureFwd",
  DEPARTURE_MID: "departureMid",
  DEPARTURE_AFT: "departureAft",
  ARRIVAL_DRAFT: "arrivalDraft",
  DEPARTURE_DRAFT: "departureDraft",
  NET_TONNAGE: "netTonnage",
  GROSS_TONNAGE: "grossTonnage",
  LIGHT: "light",
  LOADED: "loaded",
  ARRIVAL: "arrival",
  DEPARTURE: "departure",
  SUMMER_DWT: "summerDwt",
  TROPICAL_DWT: "tropicalDwt",
  MANIFOLDS_SIZE: "manifoldsSize",
  MANIFOLDS_NUMBER: "manifoldsNumber",
  MANIFOLDS_DISTANCE: "manifoldsDistance",
  MANIFOLDS_HEIGHT_GRAFTING: "manifoldsHeightGrafting",
  MANIFOLDS_HEIGHT_BALLAST: "manifoldsHeightBallast",
  MANIFOLDS_HEIGHT_LOADED: "manifoldsHeightLoaded",
  MANIFOLDS_EXTREME_HEIGHT: "manifoldsExtremeHeight",
  MANIFOLDS_DISTANCE_BOW_BCM_STERN: "manifoldsDistanceBowBcmStern",
  MANIFOLDS_DISTANCE_BOW_SCM_STERN: "manifoldsDistanceBowScmStern",
  MANIFOLDS_DISTANCE_INBOARD: "manifoldsDistanceInboard",
  TERMINAL_LOADING_ARM_CONNECTION: "terminalLoadingArmConnection",
  EXHAUST_GAS_CLEANING_SYSTEM: "exhaustGasCleaningSystem",
  EXHAUST_GAS_CLEANING_SYSTEM_TYPE: "exhaustGasCleaningSystemType",
  CONFIRM_INERT_GAS_SYSTEM_FITTED: "confirmInertGasSystemFitted",
  IS_INERT_GAS_SYSTEM_OPERATIONAL: "isInertGasSystemOperational",
  VESSEL_CONDITION: "vesselCondition",
  CARGO_TYPE: "cargoType",
  CARGO_TYPE_OTHER: "cargoTypeOther",
  CARGO_QUANTITY_M3: "cargoQuantityM3",
  CARGO_QUANTITY: "cargoQuantity",
  PARCELS: "parcels",
  FLASH_POINT: "flashPoint",
  SULPHUR: "sulphur",
  MERCAPTAN: "mercaptan",
  STOWED_CARGO_TEMPERATURE: "stowedCargoTemperature",
  HYDROGEN_SULPHIDE: "hydrogenSulphide",
  RVP: "rvp",
  COLOUR_ASTM: "colourAstm",
  BENZENE_CONTENT: "benzeneContent",
  ARE_HIGH_H2S_TANKS_ADEQUATE: "areHighH2STanksAdequate",
  AVG_PRESSURE_CARGO_TANKS_ARRIVAL: "avgPressureCargoTanksArrival",
  MAX_PRESSURE_MANIFOLDS: "maxPressureManifolds",
  IS_CRUDE_OIL_WASHING_OPERATION_PLANNED: "isCrudeOilWashingOperationPlanned",
  MOORING_FWD: "mooringFwd",
  MOORING_MID: "mooringMid",
  MOORING_AFT: "mooringAft",
  HAS_MOORING_DETAILS_AND_DEFICIENCIES: "hasMooringDetailsAndDeficiencies",
  MOORING_DETAILS_AND_DEFICIENCIES: "mooringDetailsAndDeficiencies",
  HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT: "hasAnyDefectsHullMachineryEquipment",
  ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT: "anyDefectsHullMachineryEquipment",
  HAS_AREA_WITH_PROTRUSION: "hasAreaWithProtrusion",
  AREA_WITH_PROTRUSION: "areaWithProtrusion",
  LAST_SIRE_INSPECTION: "lastSireInspection",
  ISSUING_AUTHORITY: "issuingAuthority",
  LAST_PSC_INSPECTION: "lastPscInspection",
  PLACE: "place",
  LAST_PORT: "lastPort",
  NEXT_PORT: "nextPort",
  SECURITY_LEVEL: "securityLevel",
  PAQ_DOCUMENT: "paqDocument",
  CONFIRMATION: "confirmation",
  DOCUMENTS: NOMINATION_DOCUMENTS_FIELD,
  REMARKS: "remarks",
  ADDITIONAL_NOMINATION_DOCUMENTS: "additionalNominationDocuments",
  DOCUMENT_URL: "documentUrl",
  SPECIFY_OTHER: "specifyOther",
  DOCUMENT_ISSUE_DATE: "documentIssueDate",
  DOCUMENT_EXPIRY_DATE: "documentExpiryDate",
  DOCUMENT_TYPE: "documentType",
  PENDING_UPLOADS: "pendingUploads",
} as const;

export const CARGO_ONBOARD_FORM_FIELDS = {
  ID: "id",
  TERMINAL: "terminal",
  OPERATION_TYPE: "operationType",
  CARGO_TYPE: "cargoType",
  CARGO_QUANTITY_CBM: "cargoQuantityCbm",
  CARGO_QUANTITY_MT: "cargoQuantityMt",
} as const;

export const STEP_FIELDS: Record<number, string[]> = {
  0: [
    FORM_FIELDS.RTA,
    FORM_FIELDS.CALL_SIGN,
    FORM_FIELDS.PREVIOUS_VESSEL_NAME,
    FORM_FIELDS.LOA,
    FORM_FIELDS.BALLAST,
    FORM_FIELDS.DRAUGHT_SUMMER,
    FORM_FIELDS.DRAUGHT_TROPICAL,
    FORM_FIELDS.ARRIVAL_FWD,
    FORM_FIELDS.ARRIVAL_MID,
    FORM_FIELDS.ARRIVAL_AFT,
    FORM_FIELDS.MAX_TRIM,
    FORM_FIELDS.DEPARTURE_FWD,
    FORM_FIELDS.DEPARTURE_MID,
    FORM_FIELDS.DEPARTURE_AFT,
    FORM_FIELDS.NET_TONNAGE,
    FORM_FIELDS.GROSS_TONNAGE,
    FORM_FIELDS.LIGHT,
    FORM_FIELDS.LOADED,
    FORM_FIELDS.ARRIVAL,
    FORM_FIELDS.DEPARTURE,
    FORM_FIELDS.SUMMER_DWT,
    FORM_FIELDS.TROPICAL_DWT,
    FORM_FIELDS.MANIFOLDS_SIZE,
    FORM_FIELDS.MANIFOLDS_NUMBER,
    FORM_FIELDS.MANIFOLDS_DISTANCE,
    FORM_FIELDS.MANIFOLDS_HEIGHT_GRAFTING,
    FORM_FIELDS.MANIFOLDS_HEIGHT_BALLAST,
    FORM_FIELDS.MANIFOLDS_HEIGHT_LOADED,
    FORM_FIELDS.MANIFOLDS_EXTREME_HEIGHT,
    FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_BCM_STERN,
    FORM_FIELDS.MANIFOLDS_DISTANCE_BOW_SCM_STERN,
    FORM_FIELDS.MANIFOLDS_DISTANCE_INBOARD,
    FORM_FIELDS.TERMINAL_LOADING_ARM_CONNECTION,
    FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM,
    FORM_FIELDS.EXHAUST_GAS_CLEANING_SYSTEM_TYPE,
    FORM_FIELDS.CONFIRM_INERT_GAS_SYSTEM_FITTED,
    FORM_FIELDS.IS_INERT_GAS_SYSTEM_OPERATIONAL,
    FORM_FIELDS.VESSEL_CONDITION,
    FORM_FIELDS.CARGO_TYPE,
    FORM_FIELDS.CARGO_TYPE_OTHER,
    FORM_FIELDS.CARGO_QUANTITY_M3,
    FORM_FIELDS.CARGO_QUANTITY,
    FORM_FIELDS.PARCELS,
    FORM_FIELDS.FLASH_POINT,
    FORM_FIELDS.SULPHUR,
    FORM_FIELDS.MERCAPTAN,
    FORM_FIELDS.STOWED_CARGO_TEMPERATURE,
    FORM_FIELDS.HYDROGEN_SULPHIDE,
    FORM_FIELDS.RVP,
    FORM_FIELDS.COLOUR_ASTM,
    FORM_FIELDS.BENZENE_CONTENT,
    FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE,
    FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL,
    FORM_FIELDS.MAX_PRESSURE_MANIFOLDS,
    FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED,
    FORM_FIELDS.MOORING_FWD,
    FORM_FIELDS.MOORING_MID,
    FORM_FIELDS.MOORING_AFT,
    FORM_FIELDS.HAS_MOORING_DETAILS_AND_DEFICIENCIES,
    FORM_FIELDS.MOORING_DETAILS_AND_DEFICIENCIES,
    FORM_FIELDS.HAS_ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT,
    FORM_FIELDS.ANY_DEFECTS_HULL_MACHINERY_EQUIPMENT,
    FORM_FIELDS.HAS_AREA_WITH_PROTRUSION,
    FORM_FIELDS.AREA_WITH_PROTRUSION,
    FORM_FIELDS.LAST_SIRE_INSPECTION,
    FORM_FIELDS.ISSUING_AUTHORITY,
    FORM_FIELDS.LAST_PSC_INSPECTION,
    FORM_FIELDS.PLACE,
    FORM_FIELDS.LAST_PORT,
    FORM_FIELDS.NEXT_PORT,
    FORM_FIELDS.SECURITY_LEVEL,
    FORM_FIELDS.PAQ_DOCUMENT,
    FORM_FIELDS.CONFIRMATION,
  ],
  1: [FORM_FIELDS.DOCUMENTS, FORM_FIELDS.REMARKS, FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS],
};

export const REMARKS_INSTRUCTIONS_MAX_CHARS = 1000;

export const yesNoOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const yesNoNAOptions = [...yesNoOptions, { label: "N/A", value: "na" }];

export const yesNAOptions = [
  { label: "Yes", value: "true" },
  { label: "N/A", value: "na" },
];

export const exhaustGasCleaningSystemTypeOptions = [
  { label: "Open-Loop", value: "OPEN_LOOP" },
  { label: "Closed-Loop", value: "CLOSED_LOOP" },
];

export const LOADED_VESSEL_CONDITIONS = ["LD", "PL"];
